import * as React from 'react';
import { Box, IconButton, Typography } from '@material-ui/core';
import Switch from '@material-ui/core/Switch';
import DeleteIcon from '@material-ui/icons/Delete';
import TextRotateVerticalIcon from '@material-ui/icons/TextRotateVertical';
import TextRotationNoneIcon from '@material-ui/icons/TextRotationNone';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Moveable from 'react-moveable';
import Selecto from 'react-selecto';
import opentype from 'opentype.js';
import type { OtFont, OtPath } from '../../types/opentypeJsFont';
import type { WehaLine, WehaGlobalSettings } from '../../Reducers/Configurator/types';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { getMaterialColor } from '../../utils/materialColors';
import { getFontUrl } from '../../utils/fontUrls';
import { SPACING_MM_BY_FONT_TYPE, FONT_TYPE_SINGLE_LETTERS } from '../../constants/fontTypes';
import { isVerticalTextAvailable } from '../../Reducers/Configurator/Selectors';
import {
  type TextScalingMode,
  PREVIEW_SCALE,
  computePathFontSize,
  getFontSizePx,
  parseCapHeightMm,
  parseXHeightMm,
  getTextScalingMode,
} from '../../utils/fontPathScaling';
import RulerIcon from '../Icons/RulerIcon';
import RulerIconOff from '../Icons/RulerIconOff';

const DOT_GRID_COLOR = 'rgba(0,0,0,0.06)';

/** Font URL by display name. Add TTF files to public/fonts/ to match. */
//@todo will need refactoring once we get the font data from the backend
export const FONT_SOURCES: Record<string, string> = {
  'Antike (Standard)': '/fonts/Roboto-Regular.ttf',
  Modern: '/fonts/OpenSans-Regular.ttf',
  Roboto: '/fonts/Roboto-Regular.ttf',
  OpenSans: '/fonts/OpenSans-Regular.ttf',
};

/** Fixed design space for coordinates (stored left/top). Export viewBox is always this; live canvas is sized by container. */
// Expanded to 4000x4000 to act as a Large Stage internally
export const DESIGN_SPACE = { w: 4000, h: 4000 };
/** Real-size dimensions in mm: design space at PREVIEW_SCALE. */
export const REAL_SIZE_MM = { w: DESIGN_SPACE.w / PREVIEW_SCALE, h: DESIGN_SPACE.h / PREVIEW_SCALE };

export interface SnapshotWithBounds {
  dataUrl: string;
  contentBounds: { left: number; top: number; width: number; height: number } | null;
}

export interface OpentypeCanvasHandle {
  getSvg: () => string;
  /** SVG with width/height in mm so that 1 cm in design = 1 cm when rendered at 100%. */
  getRealSizeSvg: () => {
    svg: string;
    widthMm: number;
    heightMm: number;
    /** Tight line bboxes in export mm space; used to skip empty A4 tiles inside the document union. */
    lineRegionsMm: ExportLineRegionMm[];
  } | null;
  getSnapshot: (format?: string) => Promise<string>;
  /** Snapshot at fixed design size (no viewport crop) plus content bbox from SVG for overlay. */
  getSnapshotWithContentBounds: (format?: string) => Promise<SnapshotWithBounds>;
}

interface PathTextProps {
  line: WehaLine;
  el: PathElement;
  fonts: Record<string, OtFont>;
  isSelected: boolean;
  onSync: (
    w: number,
    h: number,
    rulerW?: number,
    rulerH?: number,
    rulerHeightTop?: number,
    rulerHeightBottom?: number,
    boxBottomY?: number
  ) => void;
  alignment: 'left' | 'center' | 'right';
}

interface PathElement {
  id: string;
  content: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  textScalingMode: TextScalingMode;
  fontSizeKey: string;
  fill: string;
  letterSpacing: number;
  writingMode: 'horizontal-tb' | 'vertical-rl';
  fontFamily: string;
  rulerWidth?: number;
  rulerHeight?: number;
  rulerHeightTop?: number;
  rulerHeightBottom?: number;
  /** Horizontal mode: visual bottom of box (rectTopY + height) for width ruler placement. */
  boxBottomY?: number;
}

/** Size per character: cap for A-Z, x-height for lowercase and numbers/dates. Punctuation in numeric context matches number size so periods don't render huge. */
function getCharFontSize(char: string, baseSize: number): number {
  return baseSize;
}

function PathText({ el, fonts, isSelected, onSync, alignment }: PathTextProps) {
  const [pathData, setPathData] = React.useState('');
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const [rectTopY, setRectTopY] = React.useState(0);
  const isVertical = el.writingMode === 'vertical-rl';
  const alignOffset = isVertical ? 0 : getAlignmentOffset(alignment, el.width || 0);
  const renderX = el.x - alignOffset;
  const renderY = el.y;

  React.useLayoutEffect(() => {
    const font = fonts[el.fontFamily];
    if (!font) {
      setPathData('');
      return;
    }
    const unitsPerEm = font.unitsPerEm;
    const pathFontSize = computePathFontSize(font, el.fontSize, el.textScalingMode);
    const baselineY = (font.ascender / unitsPerEm) * pathFontSize;
    const gap = el.letterSpacing || 0;

    let generatedPath = '';
    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;

    const updateBounds = (pathObj: OtPath) => {
      const box = pathObj.getBoundingBox();
      // Skip empty/degenerate boxes (e.g. space character) so they don't pull minY to 0 and drop the line
      if (box.x2 <= box.x1 && box.y2 <= box.y1) return;
      if (box.x1 < minX) minX = box.x1;
      if (box.x2 > maxX) maxX = box.x2;
      if (box.y1 < minY) minY = box.y1;
      if (box.y2 > maxY) maxY = box.y2;
    };

    let firstNonSpaceX: number | null = null;
    let maxCharWidth = 0;

    if (!isVertical) {
      let cursorX = 0;
      const chars = el.content.split('');
      chars.forEach((char) => {
        const charSize = getCharFontSize(char, pathFontSize);
        const path = font.getPath(char, cursorX, baselineY, charSize);
        generatedPath += path.toPathData(2);
        if (firstNonSpaceX === null && char.trim() !== '') firstNonSpaceX = cursorX;
        if (char.trim() !== '') updateBounds(path);
        cursorX += font.getAdvanceWidth(char, charSize) + gap;
      });
    } else {
      // Vertical: fix first character's bottom (like horizontal baseline) so alignment by top is consistent.
      const ascenderPx = (font.ascender / font.unitsPerEm) * pathFontSize;
      const descenderPx = (-font.descender / font.unitsPerEm) * pathFontSize;
      const baselineOffset = ascenderPx;
      const firstLineBottom = ascenderPx + descenderPx; // fixed "first line" height; first char's bottom sits here
      const lineHeightPx = ascenderPx + descenderPx; // same as horizontal line height so descenders/diacritics don't overlap
      el.content.split('').forEach((char) => {
        const w = font.getAdvanceWidth(char, pathFontSize);
        if (w > maxCharWidth) maxCharWidth = w;
      });
      let cursorY = firstLineBottom - baselineOffset;
      el.content.split('').forEach((char) => {
        const baselineOffset = (font.ascender / font.unitsPerEm) * pathFontSize;
        const charSize = getCharFontSize(char, pathFontSize);
        const charWidth = font.getAdvanceWidth(char, charSize);
        const xPos = (maxCharWidth - charWidth) / 2;
        const path = font.getPath(char, xPos, cursorY + baselineOffset, charSize);
        generatedPath += path.toPathData(2);
        updateBounds(path);
        cursorY += lineHeightPx + gap;
      });
    }

    const noPath = minX === Infinity;
    if (noPath) {
      minX = 0;
      maxX = 0;
      minY = 0;
      maxY = 0;
    }

    const calculatedWidth = maxX - minX;
    /** When there are leading spaces, use width from first letter to end so box and ruler match visible text; path is still drawn with offset so first letter is at 0. */
    const visibleWidth = !isVertical && firstNonSpaceX !== null ? maxX - firstNonSpaceX : calculatedWidth;
    let nominalHeightPx: number;
    const offsetX = !isVertical && firstNonSpaceX !== null ? -firstNonSpaceX : -minX;
    let offsetY: number;

    if (isVertical) {
      /** Sync baseline distance from box top/bottom with horizontal: first char baseline at ascenderPx, box bottom at last baseline + descenderPx. */
      const ascenderPx = (font.ascender / font.unitsPerEm) * pathFontSize;
      const descenderPx = (-font.descender / font.unitsPerEm) * pathFontSize;
      const lineHeightPx = ascenderPx + descenderPx;
      const contentHeightPx = maxY - minY;
      const n = el.content.length;
      const lastBaselineInGroup = n >= 1 ? ascenderPx + (n - 1) * (lineHeightPx + gap) : 0;
      const minBoxHeight = n >= 1 ? lastBaselineInGroup + descenderPx : 0;
      nominalHeightPx =
        contentHeightPx > 0.5
          ? Math.max(contentHeightPx, minBoxHeight)
          : parseCapHeightMm(el.fontSizeKey) * PREVIEW_SCALE;
      offsetY = -descenderPx;
      setRectTopY(0);
    } else {
      // Full line height from font metrics; no top extra so box aligns with content top (same top spacing as before the offset fix, box slightly smaller).
      const ascenderPx = (font.ascender / font.unitsPerEm) * pathFontSize;
      const descenderPx = (-font.descender / font.unitsPerEm) * pathFontSize;
      nominalHeightPx = ascenderPx + descenderPx;
      // Path is already in y-down (SVG); box is laid out for that, so no vertical offset needed (avoids ascender-sized gap below descender from y-up bbox).
      offsetY = 0;
      setRectTopY(0);
    }

    const contentTrimmed = (el.content || '').trim();
    setPathData(!isVertical && contentTrimmed === '' ? '' : generatedPath);
    setOffset({ x: offsetX, y: offsetY });

    const boxChanged =
      Math.abs(visibleWidth - (el.width ?? 0)) > 0.5 || Math.abs(nominalHeightPx - (el.height ?? 0)) > 0.5;

    const needRulerBounds = !isVertical && (el.rulerHeightTop == null || el.rulerHeightBottom == null);
    const needRulerBoundsVertical = isVertical && (el.rulerHeightTop == null || el.rulerHeightBottom == null);

    if (isVertical) {
      if (boxChanged || needRulerBoundsVertical) {
        const rulerHeightPx = maxY - minY > 0.5 ? maxY - minY : nominalHeightPx;
        const rulerHeightTop = offsetY + minY;
        const rulerHeightBottom = offsetY + maxY;
        const boxBottomY = rulerHeightBottom;
        onSync(
          visibleWidth,
          nominalHeightPx,
          maxCharWidth,
          rulerHeightPx,
          rulerHeightTop,
          rulerHeightBottom,
          boxBottomY
        );
      }
    } else {
      /** Height ruler: bottom = baseline, span = selected font size (cap/x-height px), matching textScalingMode like the path. */
      const horizRulerBottom = offsetY + baselineY;
      const horizRulerTop = horizRulerBottom - el.fontSize;
      const horizRulerH = el.fontSize;
      const rulerGeometryDiffers = (a: number | undefined, b: number) => a == null || Math.abs(a - b) > 0.5;
      const rulerNeedsSync =
        needRulerBounds ||
        rulerGeometryDiffers(el.rulerHeight, horizRulerH) ||
        rulerGeometryDiffers(el.rulerHeightTop, horizRulerTop) ||
        rulerGeometryDiffers(el.rulerHeightBottom, horizRulerBottom);
      if (boxChanged || rulerNeedsSync) {
        const boxBottomY = nominalHeightPx;
        onSync(
          visibleWidth,
          nominalHeightPx,
          visibleWidth,
          horizRulerH,
          horizRulerTop,
          horizRulerBottom,
          boxBottomY
        );
      }
    }
  }, [
    el.content,
    el.fontSize,
    el.fontSizeKey,
    el.textScalingMode,
    el.letterSpacing,
    el.writingMode,
    el.fontFamily,
    el.width,
    el.height,
    el.rulerHeightTop,
    el.rulerHeightBottom,
    fonts,
    isVertical,
    onSync,
  ]);

  const hasFont = !!fonts[el.fontFamily];
  const isEmpty = !(el.content || '').trim();

  return (
    <g
      id={String(el.id)}
      className={`target ${isSelected ? 'selected' : ''}`}
      style={{
        transform: `translate(${renderX}px, ${renderY}px)`,
        transformOrigin: '0 0',
        cursor: 'pointer',
      }}
      data-x={renderX}
      data-y={renderY}
      clipPath={!isVertical && el.width && el.height ? `url(#clip-path-${el.id})` : undefined}
    >
      {!isVertical && el.width && el.height ? (
        <defs>
          <clipPath id={`clip-path-${el.id}`}>
            <rect x={0} y={rectTopY} width={el.width} height={el.height} />
          </clipPath>
        </defs>
      ) : null}
      {hasFont && pathData ? (
        <path d={pathData} fill={el.fill} transform={`translate(${offset.x}, ${offset.y})`} />
      ) : /* Fallback when font not loaded – vertical: one letter per line via tspan (SVG ignores \n) */
      hasFont && isVertical && (el.content || '').length > 0 ? (
        <text
          x={0}
          fill={el.fill}
          fontSize={el.fontSize}
          fontFamily="sans-serif"
          style={{ letterSpacing: `${el.letterSpacing ?? 0}px` }}
        >
          {(el.content || '').split('').map((char, i) => {
            const lineHeight = el.fontSize * 1.15;
            return (
              <tspan key={i} x={0} y={(i + 1) * lineHeight} dy={0}>
                {char}
              </tspan>
            );
          })}
        </text>
      ) : !hasFont ? (
        <text
          x={0}
          y={el.fontSize}
          fill={el.fill}
          fontSize={el.fontSize}
          fontFamily="sans-serif"
          style={{ letterSpacing: `${el.letterSpacing ?? 0}px` }}
        >
          {el.content || ' '}
        </text>
      ) : null}
      {/* Anchor for empty lines so they stay findable when another line is selected. Position inside rect so group bounds (and selection border) don't jump when first character is typed. */}
      {isEmpty &&
        (() => {
          const centerY = rectTopY + (el.height ?? 0) / 2;
          return (
            <g className="no-export">
              <circle
                cx={0}
                cy={centerY}
                r={6}
                fill="none"
                stroke={el.fill}
                strokeWidth={1.5}
                strokeDasharray="4 2"
              />
              <line
                x1={-8}
                y1={centerY}
                x2={8}
                y2={centerY}
                stroke={el.fill}
                strokeWidth={1}
                strokeDasharray="2 2"
              />
              <line
                x1={0}
                y1={centerY - 8}
                x2={0}
                y2={centerY + 8}
                stroke={el.fill}
                strokeWidth={1}
                strokeDasharray="2 2"
              />
            </g>
          );
        })()}
      <rect
        className="no-export"
        x={0}
        y={rectTopY}
        width={el.width || 1}
        height={el.height || parseXHeightMm(el.fontSizeKey) * PREVIEW_SCALE || 1}
        fill="transparent"
        style={{ pointerEvents: 'all' }}
      />
    </g>
  );
}

/** Canvas is in preview px; convert to mm for display (mm = px / PREVIEW_SCALE). */
function previewPxToMm(px: number): number {
  return Math.round((px / PREVIEW_SCALE) * 100) / 100;
}

/** Horizontal gap between text box and rulers; keep aligned with `WEHA_MEASUREMENT_UI_PX` for new-line placement. */
export const HEIGHT_RULER_GAP = 5;
const RULER_SAFETY_MARGIN = 80;

/**
 * Pixel layout of measurement rulers (Configurator `getNewLinePosition` must clear these).
 * Depth: ticks (≈4) + mm label (≈15 baseline).
 */
export const WEHA_MEASUREMENT_UI_PX = {
  gapBelowBox: HEIGHT_RULER_GAP,
  /** Vertical space occupied by horizontal width ruler below the box */
  widthRulerDepth: 22,
  /** Horizontal extent of vertical height ruler + mm label when placed beside text */
  heightRulerSideExtent: 72,
  safetyLeftMargin: RULER_SAFETY_MARGIN,
  /** Min horizontal gap between previous line’s measurement UI and a newly placed line */
  stackGap: 8,
} as const;

function RulerOverlay({
  el,
  alignment,
  visibleContentRect,
}: {
  el: PathElement;
  alignment: 'left' | 'center' | 'right';
  /** Visible area of the canvas in content coordinates (scrollLeft, width = clientWidth). Used to flip horizontal height rulers when they would sit outside the scrollport. */
  visibleContentRect: { left: number; width: number };
}) {
  const isVertical = el.writingMode === 'vertical-rl';
  const alignOffset = isVertical ? 0 : getAlignmentOffset(alignment, el.width || 0);
  const renderX = el.x - alignOffset;
  const renderY = el.y;
  const widthPx = el.width || 0;
  const heightPx = el.height || 0;
  const rulerWidthPx = el.rulerWidth ?? widthPx;
  const rulerHeightPx = el.rulerHeight ?? heightPx;
  const widthMm = previewPxToMm(rulerWidthPx);
  const heightMm = previewPxToMm(rulerHeightPx);
  const viewRight = visibleContentRect.left + visibleContentRect.width;
  const rightRulerOuterX = renderX + widthPx + HEIGHT_RULER_GAP + WEHA_MEASUREMENT_UI_PX.heightRulerSideExtent;
  /** Vertical: ruler on the right when the column is near the left edge. Horizontal: prefer right; if the ruler would extend past the visible scrollport, use the left. */
  const heightRulerOnRight = isVertical
    ? renderX < RULER_SAFETY_MARGIN
    : rightRulerOuterX <= viewRight - 8;
  const hasHeightRulerBounds = el.rulerHeightTop != null && el.rulerHeightBottom != null;
  const heightRulerY1 = hasHeightRulerBounds ? el.rulerHeightTop! : 0;
  const heightRulerY2 = hasHeightRulerBounds ? el.rulerHeightBottom! : heightPx;
  const heightRulerMidY = (heightRulerY1 + heightRulerY2) / 2;
  const showHeightRuler = isVertical || hasHeightRulerBounds;

  if (widthPx <= 0 && heightPx <= 0) return null;

  return (
    <g
      className="no-export"
      style={{
        transform: `translate(${renderX}px, ${renderY}px)`,
        transformOrigin: '0 0',
        pointerEvents: 'none',
      }}
    >
      <g transform={`translate(0, ${(el.boxBottomY ?? heightPx) + HEIGHT_RULER_GAP})`}>
        <line
          x1={0}
          y1={0}
          x2={widthPx}
          y2={0}
          stroke="#ff0055"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
        <line x1={0} y1={0} x2={0} y2={4} stroke="#ff0055" vectorEffect="non-scaling-stroke" />
        <line x1={widthPx} y1={0} x2={widthPx} y2={4} stroke="#ff0055" vectorEffect="non-scaling-stroke" />
        <text
          x={widthPx / 2}
          y={15}
          fill="#ff0055"
          fontSize={12}
          textAnchor="middle"
          className="MuiTypography-body2"
        >
          {widthMm} mm
        </text>
      </g>
      {showHeightRuler && (
        <g transform={`translate(${heightRulerOnRight ? widthPx + HEIGHT_RULER_GAP : -HEIGHT_RULER_GAP}, 0)`}>
          <line
            x1={0}
            y1={heightRulerY1}
            x2={0}
            y2={heightRulerY2}
            stroke="#ff0055"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
          <line
            x1={heightRulerOnRight ? 0 : -4}
            y1={heightRulerY1}
            x2={heightRulerOnRight ? 4 : 0}
            y2={heightRulerY1}
            stroke="#ff0055"
            vectorEffect="non-scaling-stroke"
          />
          <line
            x1={heightRulerOnRight ? 0 : -4}
            y1={heightRulerY2}
            x2={heightRulerOnRight ? 4 : 0}
            y2={heightRulerY2}
            stroke="#ff0055"
            vectorEffect="non-scaling-stroke"
          />
          <text
            x={heightRulerOnRight ? 8 : -8}
            y={heightRulerMidY}
            fill="#ff0055"
            fontSize={12}
            textAnchor={heightRulerOnRight ? 'start' : 'end'}
            dominantBaseline="middle"
            className="MuiTypography-body2"
          >
            {heightMm} mm
          </text>
        </g>
      )}
    </g>
  );
}

/** Derive font type from global settings (Lettering vs Einzelbuchstaben). Used for letter-spacing. */
function getFontTypeForSpacing(gs: WehaGlobalSettings): string {
  return gs.fontType ?? FONT_TYPE_SINGLE_LETTERS;
}

/** Layout key: when this changes we must re-measure the line (e.g. font family changed). */
function getLayoutKey(line: WehaLine, globalSettings: WehaGlobalSettings): string {
  return `${globalSettings.fontFamily ?? ''}-${line.vertical ?? false}-${line.text ?? ''}-${line.fontSize}-${getFontTypeForSpacing(globalSettings)}`;
}

function getLetterSpacingPxFromGlobalSettings(gs: WehaGlobalSettings): number {
  const fontType = getFontTypeForSpacing(gs);
  const mm = SPACING_MM_BY_FONT_TYPE[fontType] ?? 3;
  return mm * PREVIEW_SCALE;
}

/** Horizontal offset so that (left, top) is the anchor: left=0, center=width/2, right=width. */
function getAlignmentOffset(alignment: 'left' | 'center' | 'right', width: number): number {
  if (alignment === 'left') return 0;
  if (alignment === 'right') return width;
  return width / 2;
}

export interface ExportLineRegionMm {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Per-line bounds in preview px (same rules as aggregate PDF bounds). */
function elementAxisBoundsInPreviewPx(
  el: PathElement,
  alignment: 'left' | 'center' | 'right'
): { x1: number; y1: number; x2: number; y2: number } | null {
  const isVertical = el.writingMode === 'vertical-rl';
  const alignOffset = isVertical ? 0 : getAlignmentOffset(alignment, el.width || 0);
  const renderX = el.x - alignOffset;
  const renderY = el.y;
  const w = Math.max(el.width || 0, el.rulerWidth || 0);
  const h = el.height || 0;
  const topY = el.rulerHeightTop ?? 0;
  const bottomY = el.rulerHeightBottom ?? h;
  const x1 = renderX;
  const x2 = renderX + w;
  const y1 = renderY + Math.min(0, topY);
  const y2 = renderY + Math.max(h, bottomY);
  if (!(x2 > x1 && y2 > y1)) return null;
  return { x1, y1, x2, y2 };
}

function lineToElement(
  line: WehaLine,
  fontFamily: string,
  globalSettings: WehaGlobalSettings,
  dimensions?: {
    width: number;
    height: number;
    rulerWidth?: number;
    rulerHeight?: number;
    rulerHeightTop?: number;
    rulerHeightBottom?: number;
    boxBottomY?: number;
  }
): PathElement {
  return {
    id: String(line.id),
    content: line.text || ' ',
    x: line.left ?? 0,
    y: line.top ?? 0,
    width: dimensions?.width ?? line.width ?? 0,
    height: dimensions?.height ?? line.height ?? 0,
    fontSize: getFontSizePx(line.text ?? 'H', line.fontSize ?? '30/16'),
    textScalingMode: getTextScalingMode(line.text ?? 'H'),
    fontSizeKey: line.fontSize ?? '30/16',
    fill: globalSettings.material ? getMaterialColor(globalSettings.material) : (line.fill ?? ''),
    letterSpacing: getLetterSpacingPxFromGlobalSettings(globalSettings),
    writingMode: line.vertical ? 'vertical-rl' : 'horizontal-tb',
    fontFamily: fontFamily || '',
    rulerWidth: dimensions?.rulerWidth,
    rulerHeight: dimensions?.rulerHeight,
    rulerHeightTop: dimensions?.rulerHeightTop,
    rulerHeightBottom: dimensions?.rulerHeightBottom,
    boxBottomY: dimensions?.boxBottomY,
  };
}

const useRulersSwitchStyles = makeStyles((theme) => ({
  switchOff: {
    '& .MuiSwitch-thumb': {
      backgroundColor: theme.palette.text.secondary,
      opacity: 0.8,
    },
  },
}));

interface OpentypeCanvasProps {
  lines: WehaLine[];
  globalSettings: WehaGlobalSettings;
  selectedLineIds: string[];
  onSelectLineIds: (ids: string[]) => void;
  onLinesChange: (lines: WehaLine[]) => void;
  /** When true (e.g. special-chars modal open), hide Moveable/Selecto so they don't draw on top of overlays. */
  overlayOpen?: boolean;
  onRemoveLine?: (lineId: string | number) => void;
  onUpdateLine?: (lineId: string | number, key: string, value: unknown) => void;
  onCanvasSizeChange?: (size: { w: number; h: number }) => void;
}

export const OpentypeCanvas = React.forwardRef<OpentypeCanvasHandle, OpentypeCanvasProps>(
  function OpentypeCanvas(
    {
      lines,
      globalSettings,
      selectedLineIds,
      onSelectLineIds,
      onLinesChange,
      overlayOpen = false,
      onRemoveLine,
      onUpdateLine,
      onCanvasSizeChange,
    },
    ref
  ) {
    const theme = useTheme();
    const rulersSwitchStyles = useRulersSwitchStyles();
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const svgRef = React.useRef<SVGSVGElement>(null);
    const hasCenteredRef = React.useRef(false);

    const moveableRef = React.useRef<Moveable>(null);
    const dragFromUnselectedRef = React.useRef<{
      id: string;
      startClientX: number;
      startClientY: number;
      startX: number;
      startY: number;
    } | null>(null);
    const [fonts, setFonts] = React.useState<Record<string, OtFont>>({});
    /** Force re-resolve Moveable targets after DOM has updated (fixes drag not working until many clicks). */
    const [targetsRefresh, setTargetsRefresh] = React.useState(0);
    /** Selected line box in container-relative pixels + container viewport rect. Used to derive control strips (e.g. right strip for delete/direction buttons). */
    const [selectedLineBoxPx, setSelectedLineBoxPx] = React.useState<{
      boxLeft: number;
      boxTop: number;
      boxWidth: number;
      boxHeight: number;
      containerLeft: number;
      containerTop: number;
    } | null>(null);
    const selectedLineBoxViewBoxRef = React.useRef<{ x: number; y: number; w: number; h: number } | null>(
      null
    );
    /** True while user is dragging so we hide ruler and control buttons. */
    const [isDragging, setIsDragging] = React.useState(false);
    const [showRulers, setShowRulers] = React.useState(true);
    /** Local dimensions from PathText onSync – same as PrototypeReferenceApp so ruler gets values immediately */
    const [dimensionsByLineId, setDimensionsByLineId] = React.useState<
      Record<
        string,
        {
          width: number;
          height: number;
          rulerWidth?: number;
          rulerHeight?: number;
          rulerHeightTop?: number;
          rulerHeightBottom?: number;
          boxBottomY?: number;
        }
      >
    >({});

    const [viewportSize, setViewportSize] = React.useState({ w: 800, h: 600 });
    /** Scrollport offset in content px — pair with viewportSize.w for visible [left, right) in canvas coordinates. */
    const [scrollPortLeft, setScrollPortLeft] = React.useState(0);

    const fontFamily = globalSettings.fontFamily ?? '';
    const fontUrl = getFontUrl(fontFamily);
    const fontLoaded = !!fonts[fontFamily];

    /** After font switch, Selecto remounts and can emit a bogus "select all" once; skip it to preserve selection. */
    const prevFontLoadedRef = React.useRef<boolean | null>(null);
    const ignoreFirstSelectoSelectAllRef = React.useRef(false);

    React.useEffect(() => {
      const prev = prevFontLoadedRef.current;
      if (prev === false && fontLoaded === true) {
        ignoreFirstSelectoSelectAllRef.current = true;
      }
      prevFontLoadedRef.current = fontLoaded;
    }, [fontLoaded]);

    React.useEffect(() => {
      if (!fontUrl) return;
      opentype.load(fontUrl, (err, font) => {
        if (err) {
          console.warn('OpentypeCanvas: font load failed', fontUrl, err);
        } else if (font) {
          setFonts((prev) => ({ ...prev, [fontFamily]: font }));
        }
      });
    }, [fontUrl, fontFamily]);

    /** Keep track of actual viewport dimensions for dynamic scrolling container mapping */
    React.useLayoutEffect(() => {
      const el = scrollContainerRef.current;
      if (!el) return;
      const observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
            setViewportSize({ w: entry.contentRect.width, h: entry.contentRect.height });
          }
        }
      });
      observer.observe(el);
      return () => observer.disconnect();
    }, []);

    /** Sync horizontal scroll — rulers need visible content rect, not full stage width */
    React.useLayoutEffect(() => {
      const sc = scrollContainerRef.current;
      if (!sc) return;
      const onScroll = () => setScrollPortLeft(sc.scrollLeft);
      onScroll();
      sc.addEventListener('scroll', onScroll, { passive: true });
      return () => sc.removeEventListener('scroll', onScroll);
    }, []);

    /** Centers viewport exactly over content or top-left natively if empty */
    React.useLayoutEffect(() => {
      if (hasCenteredRef.current) return;
      const el = scrollContainerRef.current;
      if (!el || el.clientWidth === 0) return;

      if (lines.length > 0) {
        const minX = Math.min(...lines.map((l) => l.left ?? 0));
        const minY = Math.min(...lines.map((l) => l.top ?? 0));
        const maxX = Math.max(...lines.map((l) => (l.left ?? 0) + (l.width ?? 100)));
        const maxY = Math.max(...lines.map((l) => (l.top ?? 0) + (l.height ?? 50)));

        const cx = (minX + maxX) / 2;
        const cy = (minY + maxY) / 2;

        el.scrollLeft = Math.max(0, cx - el.clientWidth / 2);
        el.scrollTop = Math.max(0, cy - el.clientHeight / 2);
      } else {
        el.scrollLeft = 0;
        el.scrollTop = 0;
      }
      hasCenteredRef.current = true;
    });

    const lastBoxDimensionsRef = React.useRef<Record<string, { w: number; h: number }>>({});
    const layoutKeyForBuildRef = React.useRef<Record<string, string>>({});
    const prevLayoutKeysRef = React.useRef<Record<string, string>>({});

    const elements = React.useMemo(
      () =>
        lines.map((line) => {
          const id = String(line.id);
          const key = getLayoutKey(line, globalSettings);
          const layoutKeyChanged = layoutKeyForBuildRef.current[id] !== key;
          if (layoutKeyChanged) {
            layoutKeyForBuildRef.current[id] = key;
            delete lastBoxDimensionsRef.current[id];
          }
          const dims = layoutKeyChanged ? undefined : dimensionsByLineId[id];
          const lineForElement = layoutKeyChanged
            ? { ...line, width: 0, height: 0, widthMm: 0, heightMm: 0 }
            : line;
          return lineToElement(lineForElement, fontFamily, globalSettings, dims);
        }),
      [lines, fontFamily, globalSettings, dimensionsByLineId]
    );

    /** Exact mathematical bounds calculation to reliably scale PDF exports without browser DOM clipping bugs */
    const getContentBounds = React.useCallback(() => {
      if (elements.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

      const alignment = (globalSettings.alignment || 'left') as 'left' | 'center' | 'right';
      elements.forEach((el) => {
        const b = elementAxisBoundsInPreviewPx(el, alignment);
        if (!b) return;
        if (b.x1 < minX) minX = b.x1;
        if (b.y1 < minY) minY = b.y1;
        if (b.x2 > maxX) maxX = b.x2;
        if (b.y2 > maxY) maxY = b.y2;
      });

      if (minX === Infinity) return { x: 0, y: 0, w: 0, h: 0 };
      return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    }, [elements, globalSettings.alignment]);

    const bounds = getContentBounds();
    const stagePadding = 100;
    const contentW = bounds.x + bounds.w + stagePadding;
    const contentH = bounds.y + bounds.h + stagePadding;

    React.useEffect(() => {
      onCanvasSizeChange?.(viewportSize);
    }, [viewportSize.w, viewportSize.h, onCanvasSizeChange]);

    /** Remove dimensions for lines that no longer exist. */
    React.useEffect(() => {
      const lineIds = new Set(lines.map((l) => String(l.id)));
      for (const id of Object.keys(lastBoxDimensionsRef.current)) {
        if (!lineIds.has(id)) delete lastBoxDimensionsRef.current[id];
      }
      setDimensionsByLineId((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const id of Object.keys(next)) {
          if (!lineIds.has(id)) {
            delete next[id];
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, [lines]);

    /** When a line's layout (orientation, content, font size, or global letter spacing) changes, clear its cached dimensions and lastBoxDimensions so PathText will re-sync. */
    React.useEffect(() => {
      const clearedIds: string[] = [];
      lines.forEach((line) => {
        const id = String(line.id);
        const key = getLayoutKey(line, globalSettings);
        if (prevLayoutKeysRef.current[id] !== undefined && prevLayoutKeysRef.current[id] !== key)
          clearedIds.push(id);
      });
      if (clearedIds.length > 0) {
        clearedIds.forEach((id) => delete lastBoxDimensionsRef.current[id]);
        setDimensionsByLineId((prev) => {
          const next = { ...prev };
          clearedIds.forEach((id) => delete next[id]);
          return next;
        });
        onLinesChange(
          lines.map((line) =>
            clearedIds.includes(String(line.id))
              ? {
                  ...line,
                  width: 0,
                  height: 0,
                  widthMm: 0,
                  heightMm: 0,
                }
              : line
          )
        );
        clearedIds.forEach((id) => {
          const line = lines.find((l) => String(l.id) === id);
          if (line) prevLayoutKeysRef.current[id] = getLayoutKey(line, globalSettings);
        });
      }
    }, [lines, globalSettings, onLinesChange]);

    const syncDimensions = React.useCallback(
      (
        id: string,
        w: number,
        h: number,
        rulerW?: number,
        rulerH?: number,
        rulerHeightTop?: number,
        rulerHeightBottom?: number,
        boxBottomY?: number
      ) => {
        const approx = (a: number | undefined, b: number | undefined) =>
          a === b || (typeof a === 'number' && typeof b === 'number' && Math.abs(a - b) < 1e-6);
        setDimensionsByLineId((prev) => {
          const old = prev[id];
          const nextEntry = {
            width: w,
            height: h,
            ...(rulerW != null && { rulerWidth: rulerW }),
            ...(rulerH != null && { rulerHeight: rulerH }),
            ...(rulerHeightTop != null && { rulerHeightTop }),
            ...(rulerHeightBottom != null && { rulerHeightBottom }),
            ...(boxBottomY != null && { boxBottomY }),
          };
          if (
            old &&
            approx(old.width, w) &&
            approx(old.height, h) &&
            approx(old.rulerWidth, rulerW) &&
            approx(old.rulerHeight, rulerH) &&
            approx(old.rulerHeightTop, rulerHeightTop) &&
            approx(old.rulerHeightBottom, rulerHeightBottom) &&
            approx(old.boxBottomY, boxBottomY)
          )
            return prev;
          return { ...prev, [id]: nextEntry };
        });
        const last = lastBoxDimensionsRef.current[id];
        const boxChanged = !last || last.w !== w || last.h !== h;
        const widthMm = Math.round((w / PREVIEW_SCALE) * 100) / 100;
        const heightPxForMm = rulerH != null ? rulerH : h;
        const heightMm = Math.round((heightPxForMm / PREVIEW_SCALE) * 100) / 100;
        const line = lines.find((l) => String(l.id) === id);
        const mmMismatch = line && (line.widthMm !== widthMm || line.heightMm !== heightMm);
        if (boxChanged || mmMismatch) {
          if (boxChanged) lastBoxDimensionsRef.current[id] = { w, h };
          onLinesChange(
            lines.map((line) =>
              String(line.id) === id
                ? {
                    ...line,
                    ...(boxChanged && {
                      width: w,
                      height: h,
                    }),
                    widthMm,
                    heightMm,
                  }
                : line
            )
          );
        }
        if (boxChanged) setTimeout(() => moveableRef.current?.updateRect(), 0);
      },
      [lines, onLinesChange]
    );

    /** When alignment changes, reposition selected (or all) lines: compute visual bounds using previous alignment, then set new anchor positions. */
    const prevAlignmentRef = React.useRef<string | undefined>(undefined);
    React.useEffect(() => {
      const alignment = (globalSettings.alignment as 'left' | 'center' | 'right') || 'left';
      const prev = prevAlignmentRef.current as 'left' | 'center' | 'right' | undefined;
      prevAlignmentRef.current = alignment;
      if (prev === undefined || prev === alignment) return;

      const idsToAlign = selectedLineIds.length >= 2 ? selectedLineIds : lines.map((l) => String(l.id));
      if (idsToAlign.length === 0) return;

      const targets = elements.filter((el) => idsToAlign.includes(el.id));
      if (targets.length === 0) return;

      const prevAlign = prev || 'left';
      const visualLeft = (t: { x: number; width: number; writingMode?: string }) =>
        t.x - (t.writingMode === 'vertical-rl' ? 0 : getAlignmentOffset(prevAlign, t.width || 0));
      const visualRight = (t: { x: number; width: number; writingMode?: string }) =>
        visualLeft(t) + (t.width || 0);

      const minX = Math.min(...targets.map((t) => visualLeft(t)));
      const maxX = Math.max(...targets.map((t) => visualRight(t)));
      const centerX = (minX + maxX) / 2;

      const newLines = lines.map((line) => {
        if (!idsToAlign.includes(String(line.id))) return line;
        const el = elements.find((e) => e.id === String(line.id));
        if (!el) return line;
        let newAnchorX: number;
        if (alignment === 'left') newAnchorX = minX;
        else if (alignment === 'right') newAnchorX = maxX;
        else newAnchorX = centerX;
        return { ...line, left: newAnchorX };
      });
      onLinesChange(newLines);
      setTimeout(() => moveableRef.current?.updateRect(), 10);
    }, [globalSettings.alignment, lines, elements, selectedLineIds, onLinesChange]);

    /** When dimensions, selection, or line positions (e.g. arrow keys) change, refresh Moveable's rect so the blue border matches. */
    React.useEffect(() => {
      if (selectedLineIds.length === 0) return;
      let cancelled = false;
      const raf1 = requestAnimationFrame(() => {
        if (!cancelled)
          requestAnimationFrame(() => {
            if (!cancelled) moveableRef.current?.updateRect();
          });
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(raf1);
      };
    }, [selectedLineIds, dimensionsByLineId, lines]);

    const alignment = (globalSettings.alignment as 'left' | 'center' | 'right') || 'left';
    const handleDragEnd = React.useCallback(
      (target: HTMLElement) => {
        const style = target.style.transform;
        const translateMatch = /translate\(([^,]+)(?:,\s*([^)]+))?\)/.exec(style);
        let visualX = 0,
          visualY = 0;
        if (translateMatch) {
          visualX = parseFloat(translateMatch[1]);
          visualY = parseFloat(translateMatch[2] || '0');
        }
        const id = target.id;
        const el = elements.find((e) => e.id === id);
        const isVertical = el?.writingMode === 'vertical-rl';
        const offset = el && !isVertical ? getAlignmentOffset(alignment, el.width || 0) : 0;
        const left = visualX + offset;
        const top = visualY;
        onLinesChange(lines.map((line) => (String(line.id) === id ? { ...line, left, top } : line)));
      },
      [lines, elements, alignment, onLinesChange]
    );

    const exportSvg = React.useCallback(() => {
      const originalSvg = svgRef.current;
      if (!originalSvg) return '';
      const clonedSvg = originalSvg.cloneNode(true) as SVGElement;
      clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clonedSvg.querySelectorAll('.no-export').forEach((el) => el.remove());
      const inner = clonedSvg.firstElementChild;
      if (inner && inner.tagName === 'g' && inner.hasAttribute('transform')) {
        inner.removeAttribute('transform');
        while (inner.firstChild) clonedSvg.appendChild(inner.firstChild);
        clonedSvg.removeChild(inner);
      }

      const { x, y, w, h } = getContentBounds();
      clonedSvg.setAttribute(
        'viewBox',
        `0 0 ${Math.max(w + x, viewportSize.w)} ${Math.max(h + y, viewportSize.h)}`
      );

      clonedSvg.querySelectorAll('g.target').forEach((g) => {
        const dataX = g.getAttribute('data-x');
        const dataY = g.getAttribute('data-y');
        if (dataX !== null && dataY !== null) {
          g.removeAttribute('style');
          g.setAttribute('transform', `translate(${dataX}, ${dataY})`);
          g.removeAttribute('class');
          g.removeAttribute('data-x');
          g.removeAttribute('data-y');
        }
      });
      return clonedSvg.outerHTML;
    }, [getContentBounds, viewportSize.w, viewportSize.h]);

    const getRealSizeSvg = React.useCallback(() => {
      const raw = exportSvg();
      if (!raw) return null;

      const doc = new DOMParser().parseFromString(raw, 'image/svg+xml');
      const svg = doc.documentElement;

      const contentBox = getContentBounds();
      const padding = 20;

      const vx = contentBox.x - padding;
      const vy = contentBox.y - padding;
      const vw = contentBox.w + padding * 2;
      const vh = contentBox.h + padding * 2;

      // CRITICAL FIX 1: Strip clip-paths and defs to prevent SVG-to-PDF library rendering slice bugs
      svg.querySelectorAll('[clip-path], [clipPath]').forEach((el) => {
        el.removeAttribute('clip-path');
        el.removeAttribute('clipPath');
      });
      svg.querySelectorAll('defs').forEach((el) => el.remove());

      // Group all content and translate to origin so the PDF library doesn't drop offset viewBoxes
      const inner = doc.createElementNS('http://www.w3.org/2000/svg', 'g');
      inner.setAttribute('transform', `translate(${-vx}, ${-vy})`);
      while (svg.firstChild) inner.appendChild(svg.firstChild);

      // Apply scale to convert to millimeters natively inside the SVG (1 unit = 1/PREVIEW_SCALE mm)
      const scale = 1 / PREVIEW_SCALE;
      const scaleGroup = doc.createElementNS('http://www.w3.org/2000/svg', 'g');
      scaleGroup.setAttribute('transform', `scale(${scale})`);
      scaleGroup.appendChild(inner);

      svg.appendChild(scaleGroup);

      const mmW = vw * scale;
      const mmH = vh * scale;

      // True document bounds in mm (no square inflation). A4 tiling handles wide layouts; empty tiles
      // between separated lines are culled via lineRegionsMm.

      svg.setAttribute('viewBox', `0 0 ${mmW} ${mmH}`);
      svg.setAttribute('preserveAspectRatio', 'xMinYMin meet');

      const pxPerMm = 96 / 25.4;
      svg.setAttribute('width', String(Math.round(mmW * pxPerMm)));
      svg.setAttribute('height', String(Math.round(mmH * pxPerMm)));

      const alignment = (globalSettings.alignment || 'left') as 'left' | 'center' | 'right';
      const lineRegionsMm: ExportLineRegionMm[] = [];
      elements.forEach((el) => {
        const b = elementAxisBoundsInPreviewPx(el, alignment);
        if (!b) return;
        const xMm = (b.x1 - vx) * scale;
        const yMm = (b.y1 - vy) * scale;
        const wMm = (b.x2 - b.x1) * scale;
        const hMm = (b.y2 - b.y1) * scale;
        if (wMm < 0.01 || hMm < 0.01) return;
        const xClamped = Math.max(0, Math.min(xMm, mmW - 0.01));
        const yClamped = Math.max(0, Math.min(yMm, mmH - 0.01));
        const wClamped = Math.min(wMm, mmW - xClamped);
        const hClamped = Math.min(hMm, mmH - yClamped);
        if (wClamped < 0.01 || hClamped < 0.01) return;
        lineRegionsMm.push({ id: el.id, x: xClamped, y: yClamped, w: wClamped, h: hClamped });
      });

      return {
        svg: new XMLSerializer().serializeToString(svg),
        widthMm: mmW,
        heightMm: mmH,
        lineRegionsMm,
      };
    }, [elements, exportSvg, getContentBounds, globalSettings.alignment]);

    const SNAPSHOT_PADDING = 20;

    const getSnapshotWithContentBounds = React.useCallback(
      (format = 'image/png'): Promise<SnapshotWithBounds> => {
        const raw = exportSvg();
        if (!raw) return Promise.resolve({ dataUrl: '', contentBounds: null });

        const doc = new DOMParser().parseFromString(raw, 'image/svg+xml');
        const svg = doc.documentElement;

        const contentBox = getContentBounds();

        const vx = contentBox.x - SNAPSHOT_PADDING;
        const vy = contentBox.y - SNAPSHOT_PADDING;
        const vw = contentBox.w + SNAPSHOT_PADDING * 2;
        const vh = contentBox.h + SNAPSHOT_PADDING * 2;

        const cw = Math.ceil(vw);
        const ch = Math.ceil(vh);

        // Exact same translation offset rule here protects against Canvas drawImage cropping
        const inner = doc.createElementNS('http://www.w3.org/2000/svg', 'g');
        inner.setAttribute('transform', `translate(${-vx}, ${-vy})`);
        while (svg.firstChild) inner.appendChild(svg.firstChild);
        svg.appendChild(inner);

        svg.setAttribute('viewBox', `0 0 ${cw} ${ch}`);
        svg.setAttribute('width', `${cw}px`);
        svg.setAttribute('height', `${ch}px`);

        const blob = new Blob([new XMLSerializer().serializeToString(svg)], {
          type: 'image/svg+xml;charset=utf-8',
        });
        const url = URL.createObjectURL(blob);
        const img = new Image();

        return new Promise<SnapshotWithBounds>((resolve, reject) => {
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = cw;
            canvas.height = ch;
            const ctx = canvas.getContext('2d');
            if (ctx) ctx.drawImage(img, 0, 0, cw, ch);
            const dataUrl = canvas.toDataURL(format);
            URL.revokeObjectURL(url);

            // Image is pre-cropped correctly. Returning null allows PreviewOnStoneOverlay to skip double-cropping.
            resolve({ dataUrl, contentBounds: null });
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Image load failed'));
          };
          img.src = url;
        });
      },
      [exportSvg, getContentBounds]
    );

    React.useImperativeHandle(ref, () => ({
      getSvg: exportSvg,
      getRealSizeSvg,
      getSnapshot: (format = 'image/png') => getSnapshotWithContentBounds(format).then((r) => r.dataUrl),
      getSnapshotWithContentBounds,
    }));

    /** When user pointer-downs on an unselected .target, select it and run a custom drag so the first gesture moves without needing a prior click. */
    const handlePointerDownCapture = React.useCallback(
      (e: React.PointerEvent) => {
        const g = (e.target as Element).closest('.target');
        if (!g || !(g instanceof HTMLElement)) return;
        const id = g.id;
        if (!id || selectedLineIds.includes(id)) return;
        const el = elements.find((x) => x.id === id);
        if (!el) return;
        e.preventDefault();
        e.stopPropagation();
        onSelectLineIds([id]);
        const isVertical = el.writingMode === 'vertical-rl';
        const alignOffset = isVertical ? 0 : getAlignmentOffset(alignment, el.width || 0);
        const startX = el.x - alignOffset;
        const startY = el.y;
        dragFromUnselectedRef.current = {
          id,
          startClientX: e.clientX,
          startClientY: e.clientY,
          startX,
          startY,
        };
      },
      [elements, selectedLineIds, onSelectLineIds, alignment]
    );

    React.useEffect(() => {
      const onPointerMove = (e: PointerEvent) => {
        const state = dragFromUnselectedRef.current;
        if (!state) return;
        const dx = e.clientX - state.startClientX;
        const dy = e.clientY - state.startClientY;
        const el = document.getElementById(state.id);
        if (el && el instanceof HTMLElement) {
          const x = state.startX + dx;
          const y = state.startY + dy;
          el.style.transform = `translate(${x}px, ${y}px)`;
        }
      };
      const onPointerUp = (e: PointerEvent) => {
        const state = dragFromUnselectedRef.current;
        if (!state) return;
        dragFromUnselectedRef.current = null;
        const el = document.getElementById(state.id);
        if (el && el instanceof HTMLElement) {
          handleDragEnd(el);
        }
      };
      document.addEventListener('pointermove', onPointerMove, { capture: true });
      document.addEventListener('pointerup', onPointerUp, { capture: true });
      document.addEventListener('pointercancel', onPointerUp, { capture: true });
      return () => {
        document.removeEventListener('pointermove', onPointerMove, { capture: true });
        document.removeEventListener('pointerup', onPointerUp, { capture: true });
        document.removeEventListener('pointercancel', onPointerUp, { capture: true });
      };
    }, [handleDragEnd]);

    /** Re-resolve targets after paint so Moveable sees DOM elements (fixes drag requiring many clicks). Also when a new font finishes loading and the SVG is remounted */
    React.useEffect(() => {
      if (selectedLineIds.length === 0) return;
      const raf = requestAnimationFrame(() => {
        setTargetsRefresh((r) => r + 1);
        setTimeout(() => moveableRef.current?.updateRect(), 0);
      });
      return () => cancelAnimationFrame(raf);
    }, [selectedLineIds.join(','), fontLoaded]);

    const moveableTargets = React.useMemo(
      () => selectedLineIds.map((id) => document.getElementById(String(id))).filter(Boolean) as HTMLElement[],
      [selectedLineIds.join(','), targetsRefresh]
    );
    const elementGuidelines = elements
      .filter((e) => !selectedLineIds.includes(e.id))
      .map((e) => document.getElementById(String(e.id)))
      .filter(Boolean) as HTMLElement[];
    const selectedElements = elements.filter((e) => selectedLineIds.includes(e.id));

    /** Selected line box in viewBox (neutral anchor for control strips). */
    const selectedLineBoxViewBox =
      selectedLineIds.length === 1 && (onRemoveLine || onUpdateLine) && selectedElements[0]
        ? (() => {
            const el = selectedElements[0];
            const alignOffset =
              el.writingMode === 'vertical-rl' ? 0 : getAlignmentOffset(alignment, el.width || 0);
            return { x: el.x - alignOffset, y: el.y, w: el.width || 0, h: el.height || 0 };
          })()
        : null;
    selectedLineBoxViewBoxRef.current = selectedLineBoxViewBox;

    const boxVbX = selectedLineBoxViewBox?.x;
    const boxVbY = selectedLineBoxViewBox?.y;
    const boxVbW = selectedLineBoxViewBox?.w;
    const boxVbH = selectedLineBoxViewBox?.h;

    /** Map selected line box from viewBox to container pixels (same transform as SVG: preserveAspectRatio meet). */
    React.useLayoutEffect(() => {
      const container = containerRef.current;
      const scrollContainer = scrollContainerRef.current;

      const updateBoxPx = () => {
        const box = selectedLineBoxViewBoxRef.current;
        if (!container || !scrollContainer || !box) {
          setSelectedLineBoxPx(null);
          return;
        }
        const rect = container.getBoundingClientRect();
        setSelectedLineBoxPx({
          boxLeft: box.x,
          boxTop: box.y,
          boxWidth: box.w,
          boxHeight: box.h,
          containerLeft: rect.left,
          containerTop: rect.top,
        });
      };
      updateBoxPx();

      if (scrollContainer) scrollContainer.addEventListener('scroll', updateBoxPx);
      return () => {
        if (scrollContainer) scrollContainer.removeEventListener('scroll', updateBoxPx);
      };
    }, [boxVbX, boxVbY, boxVbW, boxVbH, contentW, contentH, viewportSize]);

    return (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <style>{`.selecto-selection { z-index: 100 !important; background: rgba(0, 85, 255, 0.1) !important; border: 1px solid rgba(0, 85, 255, 0.8) !important; }`}</style>
        <Box
          style={{
            position: 'absolute',
            top: 16,
            right: 24,
            zIndex: 10,
            backgroundColor: 'transparent',
          }}
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'transparent',
              minWidth: 160,
            }}
          >
            <Box style={{ display: 'flex', alignItems: 'center' }}>
              {showRulers ? (
                <RulerIcon
                  style={{
                    marginRight: 8,
                    fontSize: 20,
                    color: theme.palette.text.secondary,
                  }}
                />
              ) : (
                <RulerIconOff
                  style={{
                    marginRight: 8,
                    fontSize: 20,
                    color: theme.palette.text.secondary,
                    opacity: 0.8,
                  }}
                />
              )}
              <Typography
                component="span"
                variant="body2"
                style={{
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  opacity: showRulers ? 1 : 0.8,
                  verticalAlign: 'middle',
                }}
              >
                {t('weha.showRulers')}
              </Typography>
            </Box>
            <Switch
              size="small"
              checked={showRulers}
              onChange={(_, checked) => setShowRulers(checked)}
              color="primary"
              className={!showRulers ? rulersSwitchStyles.switchOff : undefined}
              inputProps={{
                'aria-label': t('weha.showRulers'),
              }}
            />
          </Box>
        </Box>

        <div
          ref={scrollContainerRef}
          style={{ flex: 1, overflow: 'auto', position: 'relative', width: '100%', height: '100%' }}
        >
          {!fontLoaded ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              color="text.secondary"
            >
              <Typography variant="body2">{t('weha.fontLoading')}</Typography>
            </Box>
          ) : (
            <div
              ref={containerRef}
              onPointerDownCapture={handlePointerDownCapture}
              style={{
                minWidth: '100%',
                minHeight: '100%',
                width: contentW > viewportSize.w ? contentW : '100%',
                height: contentH > viewportSize.h ? contentH : '100%',
                position: 'relative',
                overflow: 'hidden',
                backgroundImage: `radial-gradient(circle, ${DOT_GRID_COLOR} 1px, transparent 1px)`,
                backgroundSize: '12px 12px',
              }}
            >
              <svg
                ref={svgRef}
                id="weha-canvas-svg"
                style={{ width: '100%', height: '100%', display: 'block' }}
              >
                {elements.map((el) => (
                  <PathText
                    key={el.id}
                    line={lines.find((l) => String(l.id) === el.id)!}
                    el={el}
                    fonts={fonts}
                    isSelected={selectedLineIds.includes(el.id)}
                    onSync={(w, h, rulerW, rulerH, rulerHeightTop, rulerHeightBottom, boxBottomY) =>
                      syncDimensions(
                        el.id,
                        w,
                        h,
                        rulerW,
                        rulerH,
                        rulerHeightTop,
                        rulerHeightBottom,
                        boxBottomY
                      )
                    }
                    alignment={(globalSettings.alignment as 'left' | 'center' | 'right') || 'left'}
                  />
                ))}
                {!isDragging &&
                  showRulers &&
                  elements.map((el) => (
                    <RulerOverlay
                      key={`${el.id}-ruler`}
                      el={el}
                      alignment={(globalSettings.alignment as 'left' | 'center' | 'right') || 'left'}
                      visibleContentRect={{ left: scrollPortLeft, width: viewportSize.w }}
                    />
                  ))}
              </svg>

              {!overlayOpen && (
                <>
                  <Moveable
                    ref={moveableRef}
                    target={moveableTargets}
                    container={containerRef.current}
                    draggable
                    snappable
                    origin={false}
                    bounds={{
                      left: 0,
                      top: 0,
                      right: Math.max(contentW, viewportSize.w),
                      bottom: Math.max(contentH, viewportSize.h),
                    }}
                    elementGuidelines={elementGuidelines}
                    snapThreshold={10}
                    onDragStart={() => setIsDragging(true)}
                    onDrag={({ target, transform }) => {
                      (target as HTMLElement).style.transform = transform;
                    }}
                    onDragEnd={({ target }) => {
                      handleDragEnd(target as HTMLElement);
                      setIsDragging(false);
                    }}
                    onDragGroup={({ events }) => {
                      events.forEach(({ target, transform }) => {
                        (target as HTMLElement).style.transform = transform;
                      });
                    }}
                    onDragGroupEnd={({ events }) => {
                      events.forEach(({ target }) => handleDragEnd(target as HTMLElement));
                      setIsDragging(false);
                    }}
                  />

                  <Selecto
                    container={containerRef.current}
                    dragContainer={scrollContainerRef.current}
                    selectableTargets={['.target']}
                    hitRate={0}
                    selectByClick
                    selectFromInside={false}
                    toggleContinueSelect={['shift']}
                    preventDefault
                    onSelect={(e) => {
                      const ids = e.selected.map((x) => x.id);
                      if (
                        ignoreFirstSelectoSelectAllRef.current &&
                        elements.length > 1 &&
                        ids.length === elements.length
                      ) {
                        ignoreFirstSelectoSelectAllRef.current = false;
                        return;
                      }
                      ignoreFirstSelectoSelectAllRef.current = false;
                      if (ids.join(',') !== selectedLineIds.join(',')) {
                        onSelectLineIds(ids);
                      }
                    }}
                  />

                  {/* Right strip: direction (top) and delete (bottom) relative to selected line box; hidden while dragging. */}
                  {!isDragging &&
                    selectedLineBoxViewBox &&
                    selectedLineIds[0] &&
                    (onRemoveLine || onUpdateLine) && (
                      <div
                        ref={(node) => {
                          if (node && !node.dataset.shieldAttached) {
                            const stop = (e: Event) => e.stopPropagation();
                            node.addEventListener('mousedown', stop);
                            node.addEventListener('touchstart', stop);
                            node.addEventListener('pointerdown', stop);
                            node.dataset.shieldAttached = 'true';
                          }
                        }}
                        style={{
                          position: 'absolute',
                          left: selectedLineBoxViewBox.x + selectedLineBoxViewBox.w,
                          top: selectedLineBoxViewBox.y,
                          width: 28,
                          height: selectedLineBoxViewBox.h,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          zIndex: 100,
                          pointerEvents: 'auto',
                        }}
                      >
                        {onUpdateLine && selectedElements[0] && (
                          <div style={{ marginTop: -28 }}>
                            <IconButton
                              disabled={!isVerticalTextAvailable(globalSettings)}
                              onClick={() =>
                                onUpdateLine(
                                  selectedLineIds[0],
                                  'vertical',
                                  selectedElements[0].writingMode !== 'vertical-rl'
                                )
                              }
                              aria-label={t('weha.selectedLine.toggleDirection')}
                              size="small"
                            >
                              {selectedElements[0].writingMode === 'vertical-rl' ? (
                                <TextRotationNoneIcon />
                              ) : (
                                <TextRotateVerticalIcon />
                              )}
                            </IconButton>
                          </div>
                        )}
                        {onRemoveLine && (
                          <div style={{ marginBottom: -28 }}>
                            <IconButton
                              onClick={() => onRemoveLine(selectedLineIds[0])}
                              aria-label={t('weha.selectedLine.deleteLine')}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </div>
                        )}
                      </div>
                    )}
                </>
              )}
            </div>
          )}
        </div>
      </Box>
    );
  }
);
